import {
  calculateCombustionEfficiency,
  evaluateCombustionTest,
  validateFireValveTrip
} from '../../utils/oil/oftecCalculations';

/**
 * Live OFTEC CD/11 compliance flags (combustion efficiency + smoke threshold).
 * Exported for use by the Form and PDF layers.
 */
export const computeCd11Flags = (formData) => {
  const combustion = formData?.combustion || {};

  const doCombustion =
    combustion.combustionTestPerformed === 'Yes' &&
    Number(combustion.o2Pct) > 0 &&
    Number(combustion.flueTempC) > 0;

  if (!doCombustion) {
    return { efficiency: null, combustionResult: null, fireValveCheck: null };
  }

  const efficiency = calculateCombustionEfficiency({
    o2Pct: combustion.o2Pct,
    co2Pct: combustion.co2Pct,
    flueTempC: combustion.flueTempC,
    ambientTempC: combustion.ambientTempC || 20
  });

  const combustionResult = evaluateCombustionTest({
    smokeNumber: combustion.smokeNumber,
    coPpm: combustion.coPpm,
    netEfficiencyPct: efficiency.netEfficiencyPct
  });

  const fireValveCheck = formData?.servicing?.fireValveTripC
    ? validateFireValveTrip(formData.servicing.fireValveTripC)
    : null;

  return { efficiency, combustionResult, fireValveCheck };
};

export default {
  validate(formData) {
    const errors = {};

    if (!formData.serviceCompany?.companyName?.trim()) {
      errors.serviceCompany_companyName = 'Company Name is required';
    }
    if (!formData.serviceCompany?.oftecRegistration?.trim()) {
      errors.serviceCompany_oftecRegistration = 'OFTEC Registration Number is required';
    }
    if (!formData.serviceCompany?.engineerName?.trim()) {
      errors.serviceCompany_engineerName = 'Engineer Name is required';
    }
    if (!formData.serviceCompany?.engineerCardNumber?.trim()) {
      errors.serviceCompany_engineerCardNumber = 'Engineer Card Number is required';
    }
    if (!formData.serviceCompany?.telephone?.trim()) {
      errors.serviceCompany_telephone = 'Telephone is required';
    }
    if (!formData.customer?.customerName?.trim()) {
      errors.customer_customerName = 'Customer Name is required';
    }
    if (!formData.customer?.siteAddress?.trim()) {
      errors.customer_siteAddress = 'Site Address is required';
    }
    if (!formData.customer?.postCode?.trim()) {
      errors.customer_postCode = 'Post Code is required';
    }
    if (!formData.customer?.applianceType?.trim()) {
      errors.customer_applianceType = 'Appliance Type is required';
    }
    if (!formData.customer?.make?.trim()) {
      errors.customer_make = 'Make is required';
    }
    if (!formData.customer?.model?.trim()) {
      errors.customer_model = 'Model is required';
    }
    if (!formData.customer?.serialNumber?.trim()) {
      errors.customer_serialNumber = 'Serial Number is required';
    }
    if (!formData.combustion?.combustionTestPerformed) {
      errors.combustion_combustionTestPerformed = 'Combustion Test Performed is required';
    }
    if (!formData.certification?.serviceDate?.trim()) {
      errors.certification_serviceDate = 'Service Date is required';
    }
    if (!formData.certification?.certificateNumber?.trim()) {
      errors.certification_certificateNumber = 'Certificate Number is required';
    }
    if (!formData.certification?.engineerSignature?.trim()) {
      errors.certification_engineerSignature = 'Engineer Signature is required';
    }
    if (!formData.certification?.engineerPrint?.trim()) {
      errors.certification_engineerPrint = 'Engineer Name (Print) is required';
    }
    if (!formData.certification?.customerSignature?.trim()) {
      errors.certification_customerSignature = 'Customer Signature is required';
    }
    if (!formData.certification?.customerPrint?.trim()) {
      errors.certification_customerPrint = 'Customer Name (Print) is required';
    }

    // OFTEC CD/11 engine validations
    const combustion = formData.combustion || {};
    if (combustion.combustionTestPerformed === 'Yes') {
      if (!combustion.o2Pct || !combustion.flueTempC) {
        errors.combustion_testReadings =
          'O₂ and flue gas temperature required when combustion test performed';
      } else {
        const r = computeCd11Flags(formData);
        if (r.combustionResult && !r.combustionResult.pass) {
          errors.combustion_engine = r.combustionResult.issues.join(' ');
        }
      }
    }

    // Nozzle/pump data: pump pressure expected 8-14 bar for pressure-jet burners
    if (formData.nozzle?.pumpPressureBar) {
      const pp = Number(formData.nozzle.pumpPressureBar);
      if (pp < 8 || pp > 14) {
        errors.nozzle_pumpPressureBar =
          `Pump pressure ${pp} bar is outside the typical 8-14 bar range for pressure-jet burners.`;
      }
    }

    // Fire valve trip check
    if (formData.servicing?.fireValveTested === 'Yes' && formData.servicing?.fireValveTripC) {
      const valve = validateFireValveTrip(formData.servicing.fireValveTripC);
      if (!valve.valid) {
        errors.servicing_fireValve = valve.issues.join(' ');
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }
};